declare global {
  interface Window {
    _hsq?: unknown[][]
  }
}

class HubSpotService {
  private isInitialized = false
  private isTrackingEnabled = false
  private readonly portalId: string

  constructor () {
    this.portalId = import.meta.env.VITE_HUBSPOT_PORTAL_ID || ''
  }

  init (): void {
    if (this.isInitialized || !this.portalId) {
      return
    }

    if (typeof document === 'undefined') {
      return
    }

    if (document.getElementById('hs-script-loader')) {
      this.isInitialized = true
      return
    }

    try {
      const script = document.createElement('script')
      script.type = 'text/javascript'
      script.id = 'hs-script-loader'
      script.async = true
      script.defer = true
      script.src = `//js-eu1.hs-scripts.com/${this.portalId}.js`
      window._hsq = window._hsq ?? []
      document.head.appendChild(script)
      this.isInitialized = true
      console.log('HubSpot tracking initialized successfully')
    } catch (error) {
      console.error('Failed to initialize HubSpot tracking:', error)
    }
  }

  grantConsent (): void {
    this.init()
    if (!this.isInitialized || this.isTrackingEnabled) return

    try {
      window._hsq = window._hsq ?? []
      window._hsq.push(['doNotTrack', { track: true }])
      this.isTrackingEnabled = true
    } catch (error) {
      console.error('Failed to grant HubSpot consent:', error)
    }
  }

  getTrackingCookie (): string | undefined {
    if (!this.isTrackingEnabled || typeof document === 'undefined') return undefined

    const cookie = document.cookie.split('; ').find(item => item.startsWith('hubspotutk='))
    if (!cookie) return undefined

    const value = cookie.substring('hubspotutk='.length)
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  identify (email: string, properties?: Record<string, string | number | boolean>): void {
    if (!this.isTrackingEnabled || !email) {
      return
    }

    try {
      window._hsq = window._hsq ?? []
      window._hsq.push(['identify', { email, ...properties }])
    } catch (error) {
      console.error('Failed to identify user in HubSpot:', error)
    }
  }

  trackPageView (path?: string): void {
    if (!this.isTrackingEnabled) {
      return
    }

    try {
      window._hsq = window._hsq ?? []
      if (path) {
        window._hsq.push(['setPath', path])
      }
      window._hsq.push(['trackPageView'])
    } catch (error) {
      console.error('Failed to track HubSpot page view:', error)
    }
  }

  trackEvent (eventId: string, value?: number): void {
    if (!this.isTrackingEnabled) {
      return
    }

    try {
      window._hsq = window._hsq ?? []
      window._hsq.push(['trackCustomBehavioralEvent', { name: eventId, properties: value !== undefined ? { value } : {} }])
    } catch (error) {
      console.error('Failed to track HubSpot event:', error)
    }
  }

  revokeConsent (): void {
    this.isTrackingEnabled = false

    try {
      window._hsq = window._hsq ?? []
      window._hsq.push(['doNotTrack'])
    } catch (error) {
      console.error('Failed to revoke HubSpot consent:', error)
    }
  }
}

export const hubspotService = new HubSpotService()
